<template>
  <SelectBase
    :on-toggle="onToggle"
    :downshift-props="{
      disabled: disabled || readonly,
      ...downshiftProps,
      items: [],
      itemToText,
    }"
    :popover-props="forwardPopoverProps"
  >
    <template
      #select="{
        toggleOpen, isOpen
      }"
    >
      <SelectButton
        :value="itemToText(localValue)"
        :bs="bs"
        :readonly="readonly"
        :placeholder="placeholder"
        v-bind="$attrs"
        :disabled="disabled"
        :is-open="isOpen"
        :show-arrow="false"
        @click="toggleOpen"
      >
        <template
          v-if="!readonly && showClear"
          #right
        >
          <BeerIcon
            :size="14"
            :bs="{
              mx: 12, transform: 'rotate(45deg)',
              fill: 'ash',
              cursor: 'pointer',
              '&:hover': { fill: 'mute' }
            }"
            icon="add-f"
            @click.stop="clearValue"
          />
        </template>
      </SelectButton>
    </template>

    <template #options>
      <NewBsBox
        :bs="{
          display: 'flex',
          justifyContent: 'space-between',
          position: 'relative',
          zIndex: 1,
        }"
      >
        <Divider :bs="{ zIndex: -1, left: 24, right: 24, top: 99, position: 'absolute', }" />
        <Divider :bs="{ zIndex: -1, left: 24, right: 24, top: 140, position: 'absolute', }" />
        <ClockColumn
          ref="hour"
          @scroll.passive="autoStickScroll('hour')"
        >
          <ClockCell
            v-for="(h, index) of matrix[0]"
            :key="h.label"
            :data-active="localValue?.hour === h.value || null"
            @click.stop.prevent="() => scrollTo('hour', index, h)"
          >
            {{ h.label }}
          </ClockCell>
        </ClockColumn>
        <ClockColumn
          v-if="mode !== 'hour'"
          ref="minute"
          @scroll.passive="autoStickScroll('minute')"
        >
          <ClockCell
            v-for="(m, index) of matrix[1]"
            :key="m.label"
            :data-active="localValue?.minute === m.value || null"
            @click.stop.prevent="() => scrollTo('minute', index, m)"
          >
            {{ m.label }}
          </ClockCell>
        </ClockColumn>
        <ClockColumn
          v-if="mode !== 'hour' && mode !== 'minute'"
          ref="second"
          @scroll.passive="autoStickScroll('second')"
        >
          <ClockCell
            v-for="(s, index) of matrix[2]"
            :key="s.label"
            :data-active="localValue?.second === s.value || null"
            @click.stop.prevent="() => scrollTo('second', index, s)"
          >
            {{ s.label }}
          </ClockCell>
        </ClockColumn>
      </NewBsBox>
    </template>
    <template
      #action="{ toggleOpen }"
    >
      <BeerButton
        :bs="{ mr: 0, minWidth: 'unset'}"
        modifier="body"
        variant="text"
        size="s"
        @click.stop.prevent="() => reset(toggleOpen)"
      >
        取消
      </BeerButton>
      <BeerButton
        :bs="{ mr: 12, minWidth: 'unset'}"
        modifier="brand"
        variant="text"
        size="s"
        @click.stop.prevent="() => confirm(toggleOpen)"
      >
        确定
      </BeerButton>
    </template>

  </SelectBase>
</template>

<script>
  import { get } from 'lodash'
  import { formatTimeObj } from '@xhs/yam-beer/utils/dayjs'
  import { clockTrans } from '@xhs/yam-beer/utils/modelUtils'
  import { findIndex } from '@xhs/yam-beer/utils/arrayUtils'
  import BeerButton from '@xhs/yam-beer/cmp/common/Button/Button.vue'
  import Divider from '@xhs/yam-beer/cmp/common/Divider/Divider.vue'
  import { withFixedWidthElseAuto } from '@xhs/yam-beer/utils/popoverUtils'
  import NewBsBox from '@xhs/yam-beer/cmp/primitive/NewBsBox'
  import { getClockMatrix } from '@xhs/yam-beer/utils/clockUtils'
  import SelectBase from '@xhs/yam-beer/cmp/dataEntry/Select/SelectBase.vue'
  import SelectButton from '@xhs/yam-beer/cmp/dataEntry/Select/SelectButton.vue'
  import WithBsBox from '@xhs/yam-beer/cmp/primitive/WithBsBox'
  import BeerIcon from '@xhs/yam-beer/cmp/common/Icon/Icon.vue'

  const ClockColumn = WithBsBox({
    name: 'ClockColumn',
    bs: {
      flex: 1,
      pt: 96,
      pb: 82,
      height: 226,
      px: 24,
      overflowY: 'auto',
    },
  })

  const ClockCell = WithBsBox({
    name: 'ClockCell',
    bs: {
      size: 24,
      my: 12,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      '&:not([data-active]):hover': {
        bg: 'palette.tinBlue.base',
      },
      '&[data-active]': {
        color: 'brand',
      },
    },
  })

  export default {
    name: 'ClockSelect',
    components: {
      SelectBase,
      SelectButton,
      NewBsBox,
      ClockColumn,
      ClockCell,
      Divider,
      BeerButton,
      BeerIcon,
    },
    props: {
      /**
       * 选项 SelectButton 的 inline 样式，作为 inputProps 的快捷方式
       */
      bs: {
        type: Object,
        default: () => ({}),
      },
      readonly: {
        type: Boolean,
        default: false,
      },
      /**
       * 选择时间的 Part
       * @type {hour|minute|second}
       * @see style
       */
      mode: {
        type: String,
        default: 'second',
      },
      defaultTime: {
        type: Object,
        default: () => ({}),
      },
      /**
       * 是否禁止选
       */
      disabled: {
        type: Boolean,
        default: false,
      },
      /**
       * 选项 SelectButton 的 inputProps
       */
      inputProps: {
        type: Object,
        default: () => ({}),
      },
      /**
       * placeholder 的快捷 props，作为 inputProps 的默认值
       */
      placeholder: {
        type: String,
        default: '选择时间',
      },
      /**
       * 透传给 downshift 的参数
       */
      downshiftProps: {
        type: Object,
        default: () => ({}),
      },
      /**
       * 透传给 popover 参数
       */
      popoverProps: {
        type: Object,
        default: () => ({}),
      },
      /**
       * 绑定数据 String类型需自定义transModel
       */
      modelValue: {
        type: [Object, String],
        default: undefined,
      },
      /**
       * modelValue 的解析方式
       */
      transModel: {
        type: Object,
        default: () => clockTrans(),
      },
      /**
       * 是否显示清除按钮
       */
      clearable: {
        type: Boolean,
        default: false,
      },
    },
    emits: ['update:modelValue'],
    data() {
      return {
        localValue: {},
        matrix: getClockMatrix(),
        ongoingScrollTimer: null,
      }
    },
    computed: {
      forwardPopoverProps() {
        return withFixedWidthElseAuto({ width: 216 }, {
          ...this.popoverProps,
          // shouldCloseOnExternalClick: false,
        })
      },
      showClear() {
        return this.clearable && (get(this.localValue, 'hour') || get(this.localValue, 'hour') === 0)
      },
    },
    watch: {
      modelValue: {
        immediate: true,
        handler(val) {
          if (val || get(val, 'hour') || get(val, 'hour') === 0) {
            this.localValue = this.transModel.toState(val || {})
          }
        },
      },
    },
    methods: {
      itemToText: formatTimeObj,
      confirm(toggle) {
        this.$emit(
          'update:modelValue',
          this.transModel.toModel(this.localValue),
        )
        toggle({ close: true })
      },
      reset(toggle) {
        const val = this.modelValue
        if (val || get(val, 'hour') || get(val, 'hour') === 0) {
          this.localValue = this.transModel.toState(this.modelValue || {})
        } else {
          this.localValue = {}
        }
        toggle({ close: true })
      },
      onToggle(isOpen) {
        if (isOpen) {
          if (!this.localValue?.hour) {
            this.localValue = { ...this.defaultTime }
          }
          const lst = ['hour', 'minute', 'second']
          lst.forEach((ref, i) => {
            let index = findIndex(
              this.matrix[i],
              // string => number
              item => item.value === this.localValue[ref],
            )
            if (index < 0) {
              index = 0
            }
            this.$nextTick(() => {
              this.scrollTo(ref, index, this.matrix[i][index])
            })
          })
        } else {
          const val = this.modelValue
          if (val || get(val, 'hour') || get(val, 'hour') === 0) {
            this.localValue = this.transModel.toState(this.modelValue || {})
          } else {
            this.localValue = {}
          }
        }
      },
      scrollTo(ref, index, item) {
        if (this.$refs[ref]) {
          this.$refs[ref].scrollTop = 36 * index
        }
        if (this.localValue[ref] !== item.value) {
          this.localValue[ref] = item.value
        }
      },
      autoStickScroll(ref) {
        if (!this.$refs[ref]) {
          return
        }
        if (this.ongoingScrollTimer) {
          clearTimeout(this.ongoingScrollTimer)
        }
        this.ongoingScrollTimer = setTimeout(() => {
          const el = this.$refs[ref]
          const offset = el.scrollTop % 36
          if (offset < 18) {
            el.scrollTop -= offset
          } else {
            el.scrollTop += (36 - offset)
          }
          const itemIndex = Math.floor(el.scrollTop / 36)
          let item
          if (ref === 'hour') {
            item = this.matrix[0][itemIndex]
          } else if (ref === 'minute') {
            item = this.matrix[1][itemIndex]
          } else {
            item = this.matrix[2][itemIndex]
          }
          if (this.localValue?.[ref] !== item.value) {
            this.localValue[ref] = item.value
          }
        }, 150)
      },
      clearValue() {
        this.localValue = {}
        this.$emit(
          'update:modelValue',
          this.transModel.toModel(this.localValue),
        )
      },
    },
  }
</script>
